/**
 * FeatureValueInput - Dynamic form input for feature constraint values (C7)
 *
 * Renders the appropriate input type based on the constraint type:
 * - ALLOWED_VALUES: select dropdown
 * - NUMERIC_RANGE: number input with min/max (integers, step=1)
 * - DECIMAL_RANGE: number input with min/max (decimals, step=0.01)
 * - DATE_RANGE: date input with min/max
 * - REGEX: text input with pattern hint
 * - BOOLEAN: checkbox
 * - UNCONSTRAINED: text input
 */
import React from "react";
import type { ConstraintType } from "../../../../types/adminBilling";

export interface FeatureValueInputProps {
  featureCode: string;
  constraintType: ConstraintType;
  constraintConfig: Record<string, unknown>;
  value: string | number | boolean | undefined;
  onChange: (value: string | number | boolean) => void;
}

export const FeatureValueInput: React.FC<FeatureValueInputProps> = ({
  featureCode,
  constraintType,
  constraintConfig,
  value,
  onChange,
}) => {
  const inputId = `fv-${featureCode.replace(/[^a-zA-Z0-9]/g, "-")}`;

  switch (constraintType) {
    case "ALLOWED_VALUES": {
      const allowedValues = (constraintConfig.values ?? []) as string[];
      return (
        <div>
          <label htmlFor={inputId} className="block text-xs font-medium text-neutral-700 mb-1">
            {featureCode}
          </label>
          <select
            id={inputId}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">-- wybierz --</option>
            {allowedValues.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
      );
    }

    case "NUMERIC_RANGE": {
      const min = constraintConfig.min as number | undefined;
      const max = constraintConfig.max as number | undefined;
      return (
        <div>
          <label htmlFor={inputId} className="block text-xs font-medium text-neutral-700 mb-1">
            {featureCode}
            {min != null && max != null && (
              <span className="text-neutral-400 font-normal">
                {" "}
                ({min}-{max})
              </span>
            )}
          </label>
          <input
            id={inputId}
            type="number"
            step={1}
            value={typeof value === "number" ? value : ""}
            min={min}
            max={max}
            onChange={(e) => {
              const num = parseInt(e.target.value, 10);
              if (!isNaN(num)) onChange(num);
            }}
            className="w-full border border-neutral-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      );
    }

    case "DECIMAL_RANGE": {
      const config = constraintConfig as { min?: number; max?: number };
      return (
        <div>
          <label htmlFor={inputId} className="block text-xs font-medium text-neutral-700 mb-1">
            {featureCode}
            {config.min != null && config.max != null && (
              <span className="text-neutral-400 font-normal">
                {" "}
                ({config.min}-{config.max})
              </span>
            )}
          </label>
          <input
            id={inputId}
            type="number"
            step={0.01}
            min={config.min}
            max={config.max}
            value={typeof value === "number" ? value : ""}
            onChange={(e) => {
              const num = parseFloat(e.target.value);
              if (!isNaN(num)) onChange(num);
            }}
            className="w-full border border-neutral-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      );
    }

    case "DATE_RANGE": {
      const config = constraintConfig as { min?: string; max?: string };
      return (
        <div>
          <label htmlFor={inputId} className="block text-xs font-medium text-neutral-700 mb-1">
            {featureCode}
          </label>
          <input
            id={inputId}
            type="date"
            min={config.min}
            max={config.max}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      );
    }

    case "REGEX": {
      const config = constraintConfig as { pattern?: string };
      return (
        <div>
          <label htmlFor={inputId} className="block text-xs font-medium text-neutral-700 mb-1">
            {featureCode}
          </label>
          <input
            id={inputId}
            type="text"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          {config.pattern && (
            <p className="text-xs text-neutral-500 mt-0.5">
              Wzorzec: <code className="bg-neutral-100 px-1 rounded">{config.pattern}</code>
            </p>
          )}
        </div>
      );
    }

    case "BOOLEAN": {
      return (
        <label htmlFor={inputId} className="flex items-center gap-2 text-xs text-neutral-700">
          <input
            id={inputId}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            className="h-3.5 w-3.5 text-primary-600 border-neutral-300 rounded"
          />
          {featureCode}
        </label>
      );
    }

    case "UNCONSTRAINED":
    default: {
      return (
        <div>
          <label htmlFor={inputId} className="block text-xs font-medium text-neutral-700 mb-1">
            {featureCode}
          </label>
          <input
            id={inputId}
            type="text"
            value={typeof value === "string" ? value : value != null ? String(value) : ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-neutral-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      );
    }
  }
};
